<template>
  <div
    data-component="card"
    class="relative flex flex-col p-5 lg:flex-row lg:items-center"
  >
    <div class="lg:w-1/2">
      <div class="relative">
        <div
          v-if="percentage_sold > 0"
          class="absolute top-0 right-0 z-10 flex items-center px-3 py-2 mt-5 -mr-2 text-sm tracking-wider text-white uppercase rounded-md shadow-lg lg:mt-10 lg:-mr-5 bg-danger-500"
        >
          <svg
            v-if="percentage_sold < 100"
            height="20"
            width="20"
            viewBox="0 0 20 20"
            class="mr-2"
          >
            <circle
              r="10"
              cx="10"
              cy="10"
              fill="tomato"
            />
            <circle
              r="5"
              cx="10"
              cy="10"
              fill="transparent"
              stroke="white"
              stroke-width="10"
              :stroke-dasharray="`${(percentage_sold * 31.4) / 100} 31.4`"
              transform="rotate(-90) translate(-20)"
            />
          </svg>
          <p>{{ percentage_sold }}% {{ locales[estateData.lang].sold }}</p>
        </div>
        <vue-glide
          v-if="estate.meta.features.images.length > 0"
          ref="slider"
          :options="glideOptions"
        >
          <template v-if="estate.meta.estate.status === 'available'">
            <vue-glide-slide
              v-for="image in estate.meta.features.images.slice(0, 5)"
              :key="image.ID"
            >
              <div class="aspect-ratio-16/9" />
              <div
                class="absolute top-0 left-0 z-10 w-full h-full transition duration-200 bg-black bg-opacity-50 opacity-0 lg:group-hover:opacity-100"
              />
              <img
                class="absolute top-0 left-0 z-0 object-cover object-center w-full h-full transition-all duration-200 transform group-hover:scale-105 lazy"
                :data-src="image.sizes.thumbnail"
                :alt="`Condrogest - ${
                  estate.meta.estate.title[estateData.lang]
                }`"
              >
            </vue-glide-slide>
          </template>

          <template v-else>
            <vue-glide-slide>
              <div class="aspect-ratio-16/9" />
              <div
                class="absolute top-0 left-0 z-10 w-full h-full bg-brand-500 opacity-40"
              />
              <div class="absolute top-0 left-0 w-full h-full opacity-50">
                <img
                  class="absolute top-0 left-0 z-0 object-cover object-center w-full h-full lazy"
                  :data-src="estate.meta.features.images[0].sizes.thumbnail"
                  :alt="`Condrogest - ${
                    estate.meta.estate.title[estateData.lang]
                  }`"
                >
              </div>
            </vue-glide-slide>
          </template>
        </vue-glide>
      </div>
    </div>

    <div class="lg:w-1/2 lg:pl-16">
      <div class="mb-3 lg:mb-6">
        <p
          v-if="!estate.meta.location.hidden"
          class="mb-2 lg:text-xl"
        >
          {{ estate.meta.estate.properties.length }}
          {{ locales[estateData.lang].units_in }}
          {{ estate.meta.location.city }}
        </p>
        <a
          :href="formatLink(estate.permalink)"
          class="inline-block font-serif text-xl transition duration-200 lg:text-3xl lg:leading-tight lg:text-4xl hover:text-danger-500"
        >
          <h3>{{ estate.meta.estate.title[estateData.lang] }}</h3>
        </a>
      </div>

      <div class="flex items-center justify-between">
        <div v-if="!estate.meta.price.hidden && estate.meta.price.min_max.max">
          <p class="text-sm tracking-wider uppercase">
            {{ locales[estateData.lang].starting_from }}
          </p>
          <p class="lg:text-xl">
            &euro;{{ $formatNumber(estate.meta.price.min_max.min, "DOT") }} -
            &euro;{{ $formatNumber(estate.meta.price.min_max.max, "DOT") }}
          </p>
        </div>
        <div>
          <a
            :href="formatLink(estate.permalink)"
            class="text-sm tracking-wider uppercase btn-link is-line is-thick"
          >
            {{ locales[estateData.lang].more_info }}
          </a>
        </div>
      </div>

      <div class="flex items-center justify-between mt-5 lg:mt-10">
        <div
          v-if="
            estate.meta.features.images.length > 1 &&
              estate.meta.estate.status === 'available'
          "
          class="hidden -ml-8 divide-x lg:flex"
        >
          <button
            class="relative px-3 py-3 transition duration-150 pointer-events-auto hover:opacity-50 focus:outline-none active:top-px"
            @click="slideNav('<')"
          >
            <IconLeft />
          </button>
          <button
            class="relative px-3 py-3 transition duration-150 pointer-events-auto hover:opacity-50 focus:outline-none active:top-px"
            @click="slideNav('>')"
          >
            <IconRight />
          </button>
        </div>

        <div
          v-if="
            estate.meta.open_homes.hasOpenHome &&
              estate.meta.estate.status === 'available'
          "
        >
          <div class="px-3 py-2 text-white rounded-md bg-danger-500">
            <i class="mr-1 fas fa-eye" />
            <span class="text-sm tracking-wider uppercase">
              {{ locales[estateData.lang].open_home }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LazyLoad from "vanilla-lazyload";
import { Glide, GlideSlide } from "vue-glide-js";

import IconLeft from "../svg/IconLeft";
import IconRight from "../svg/IconRight";

const slugify = require("slugify");

export default {
  components: {
    IconLeft,
    IconRight,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
  },
  props: ["loading", "estate", "estateData", "locales"],
  data() {
    return {
      glideOptions: {
        type: "carousel",
        perView: 1,
        gap: 0,
        keyboard: false,
      },
    };
  },
  computed: {
    percentage_sold() {
      const statusList = [];
      const totalItems = this.estate.meta.estate.properties.length;
      this.estate.meta.estate.properties.forEach((element) => {
        statusList.push(element.status);
      });
      const occurences = statusList.filter(
        (element) => element === "available"
      );
      return 100 - Math.ceil((occurences.length * 100) / totalItems);
    },
  },
  mounted() {
    new LazyLoad({
      elements_selector: ".lazy",
      unobserve_entered: true,
    });
  },
  methods: {
    prevent() {
      return false;
    },
    formatLink(link) {
      const title = slugify(
        this.estate.meta.estate.title[this.estateData.lang],
        {
          lower: true,
          strict: true,
        }
      );
      const url = link.split("/");
      let prefix = "";
      if (this.estateData.lang === "nl") {
        prefix = "te-huur";
        if (this.estate.meta.features.negotiation === "sale") {
          prefix = "te-koop";
        }
      } else if (this.estateData.lang === "fr") {
        prefix = "a-louer";
        if (this.estate.meta.features.negotiation === "sale") {
          prefix = "a-vendre";
        }
      }
      const str = `/${this.estateData.lang}/${prefix}/${url[5]}/${title}`;
      return str;
    },
    formatType(type) {
      let output = "";
      switch (type) {
        case "apartment":
          if (this.estateData.lang === "nl") {
            output = "Appartement";
          } else if (this.estateData.lang === "fr") {
            output = "Appartement";
          }
          break;
        case "commercial":
          if (this.estateData.lang === "nl") {
            output = "Winkelpand";
          } else if (this.estateData.lang === "fr") {
            output = "Magasin";
          }
          break;
        case "house":
          if (this.estateData.lang === "nl") {
            output = "Huis";
          } else if (this.estateData.lang === "fr") {
            output = "Maison";
          }
          break;
        case "land":
          if (this.estateData.lang === "nl") {
            output = "Grond";
          } else if (this.estateData.lang === "fr") {
            output = "Terrain";
          }
          break;
        case "office":
          if (this.estateData.lang === "nl") {
            output = "Kantoor";
          } else if (this.estateData.lang === "fr") {
            output = "Office";
          }
          break;
        case "parking":
          if (this.estateData.lang === "nl") {
            output = "Parking";
          } else if (this.estateData.lang === "fr") {
            output = "Parking";
          }
          break;
        default:
          break;
      }
      return output;
    },
    slideNav(dir) {
      if (dir === "<") {
        this.$refs.slider.glide.go("<");
      } else {
        this.$refs.slider.glide.go(">");
      }
    },
    formatNegotiation(negotiation) {
      let output = "";
      switch (negotiation) {
        case "sale":
          if (this.estateData.lang === "nl") {
            output = "te koop";
          } else if (this.estateData.lang === "fr") {
            output = "à vendre";
          }
          break;
        case "let":
          if (this.estateData.lang === "nl") {
            output = "te huur";
          } else if (this.estateData.lang === "fr") {
            output = "à louer";
          }
          break;
        default:
          break;
      }
      return output;
    },
  },
};
</script>

<style>
[data-component="card"] .glide {
  @apply relative mb-4;
}
[data-component="card"] .glide__track {
  @apply relative z-0;
}
[data-component="card"] .glide__slide {
  @apply relative overflow-hidden;
}
[data-component="card"] img.lazy:not(.nofade):not(.initial) {
  transition-property: all;
}
</style>
